import React, { useEffect, useRef, useState } from 'react';
import Lottie from 'lottie-react';
import { 
  Heart, 
  Shield, 
  Wallet, 
  HeartHandshake,
  Building2,
  ShieldCheck,
  Eye,
  FileText,
  ArrowRight,
  User,
  Briefcase
} from 'lucide-react';

// Lottie animation data for magnifying glass
const searchAnimation = {
  "v": "5.5.7",
  "fr": 30,
  "ip": 0,
  "op": 60,
  "w": 100,
  "h": 100,
  "assets": [],
  "layers": [{
    "ty": 4,
    "nm": "magnifier",
    "sr": 1,
    "ks": {
      "o": { "a": 0, "k": 100 },
      "r": { "a": 1, "k": [{ "t": 0, "s": [0], "e": [10] }, { "t": 15, "s": [10], "e": [-10] }, { "t": 30, "s": [-10], "e": [10] }, { "t": 45, "s": [10], "e": [0] }, { "t": 60, "s": [0] }] },
      "p": { "a": 0, "k": [50, 50, 0] },
      "a": { "a": 0, "k": [0, 0, 0] },
      "s": { "a": 0, "k": [100, 100, 100] }
    },
    "shapes": [{
      "ty": "gr",
      "it": [
        { "ty": "el", "s": { "a": 0, "k": [30, 30] }, "p": { "a": 0, "k": [-5, -5] } },
        { "ty": "st", "c": { "a": 0, "k": [0.5, 0.5, 0.55, 1] }, "w": { "a": 0, "k": 4 } },
        { "ty": "tr", "p": { "a": 0, "k": [0, 0] } }
      ]
    }, {
      "ty": "gr",
      "it": [
        { "ty": "rc", "s": { "a": 0, "k": [4, 15] }, "p": { "a": 0, "k": [12, 12] }, "r": { "a": 0, "k": 2 } },
        { "ty": "fl", "c": { "a": 0, "k": [0.5, 0.5, 0.55, 1] } },
        { "ty": "tr", "p": { "a": 0, "k": [0, 0] }, "r": { "a": 0, "k": 45 } }
      ]
    }],
    "ip": 0,
    "op": 60
  }]
};

const personalServices = [
  {
    icon: Heart,
    title: 'Infidelitate și verificări relaționale',
    description: 'Dovezi clare, obținute cu discreție de Detectivii din Bacau, pentru decizii raționale.',
    color: 'rose'
  },
  {
    icon: Shield,
    title: 'Verificări antecedente și securitate personală',
    description: 'Protejează-ți familia și bunurile cu verificări complete, făcute de detectivii noștri din Bacau.',
    color: 'blue'
  },
  {
    icon: Wallet,
    title: 'Detectare camere și microfoane ascunse în Bacau',
    description: 'Scanare profesională TSCM pentru identificarea dispozitivelor de supraveghere.',
    color: 'amber'
  },
  {
    icon: HeartHandshake,
    title: 'Supraveghere minori în Bacau',
    description: 'Monitorizare discretă făcută de un Detectiv Privat din Bacau, pentru siguranța și protecția copiilor.',
    color: 'green'
  }
];

const businessServices = [
  {
    icon: Building2,
    title: 'Investigații interne',
    description: 'Fraudă, delapidare, abateri disciplinare — descoperim sursa și oferim probe solide.',
    color: 'purple'
  },
  {
    icon: ShieldCheck,
    title: 'Securitate corporativă în Bacau',
    description: 'Angajați un Detectiv Privat din Bacau pentru verificarea angajaților, due diligence pentru partenerii de afaceri și protecția proprietății intelectuale.',
    color: 'cyan'
  },
  {
    icon: Eye,
    title: 'Supraveghere și monitorizare proiecte',
    description: 'Asigurăm conformitatea și protejăm activele în proiecte sensibile în județul Bacau și nu numai.',
    color: 'orange'
  },
  {
    icon: FileText,
    title: 'Raportare profesională',
    description: 'Documentație admisibilă juridic, recomandări pentru acțiuni legale sau disciplinare.',
    color: 'teal'
  }
];

const colorClasses = {
  rose: { bg: 'bg-rose-500/10', border: 'border-rose-500/30', icon: 'text-rose-400', gradient: 'from-rose-500 to-pink-500' },
  blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', icon: 'text-blue-400', gradient: 'from-blue-500 to-cyan-500' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', icon: 'text-amber-400', gradient: 'from-amber-500 to-orange-500' },
  green: { bg: 'bg-green-500/10', border: 'border-green-500/30', icon: 'text-green-400', gradient: 'from-green-500 to-emerald-500' },
  purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', icon: 'text-purple-400', gradient: 'from-purple-500 to-violet-500' },
  cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', icon: 'text-cyan-400', gradient: 'from-cyan-500 to-blue-500' },
  orange: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', icon: 'text-orange-400', gradient: 'from-orange-500 to-red-500' },
  teal: { bg: 'bg-teal-500/10', border: 'border-teal-500/30', icon: 'text-teal-400', gradient: 'from-teal-500 to-cyan-500' }
};

const ServiceCard = ({ service, index, isVisible }) => {
  const IconComponent = service.icon;
  const colors = colorClasses[service.color];
  
  return (
    <div
      className={`group relative ${colors.bg} border ${colors.border} rounded-2xl p-6 
        hover:bg-opacity-20 transition-all duration-500 hover:-translate-y-1 hover:shadow-lg
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Icon */}
      <div className={`w-14 h-14 rounded-xl ${colors.bg} flex items-center justify-center mb-5
        group-hover:scale-110 transition-transform duration-300`}>
        <IconComponent className={`w-7 h-7 ${colors.icon}`} />
      </div>

      {/* Title */}
      <h3 className="text-xl font-bold text-white mb-3 group-hover:text-white transition-colors">
        {service.title}
      </h3>

      {/* Description */}
      <p className="text-slate-300 text-base leading-relaxed">
        {service.description}
      </p>
    </div>
  );
};

const ServicesSection = () => {
  const [visibleItems, setVisibleItems] = useState([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Trigger all items to become visible
            setVisibleItems([0, 1, 2, 3, 4, 5, 6, 7]);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToContact = (e) => {
    e.preventDefault();
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="servicii" className="py-12 bg-slate-900" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with Lottie */}
        <div className="text-center mb-10">
          <div className="flex justify-center mb-3">
            <div className="w-16 h-16">
              <Lottie animationData={searchAnimation} loop={true} />
            </div>
          </div>
          <p className="text-amber-400 text-sm uppercase tracking-wider mb-3 font-semibold">Servicii Detectivi</p>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Cum Vă Poate Ajuta un Detectiv Particular din Bacau:
          </h2>
          <p className="text-slate-300 text-lg md:text-xl max-w-2xl mx-auto">
            Detectivii atestați din Bacau oferă investigații private și soluții profesionale pentru persoane fizice și companii, adaptate nevoilor specifice.
          </p>
        </div>

        {/* Personal Services */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-rose-500/20 rounded-xl flex items-center justify-center">
              <User className="w-7 h-7 text-rose-400" />
            </div>
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white">Pentru Persoane Fizice</h3>
              <p className="text-slate-400 text-base">Servicii discrete și personalizate</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {personalServices.map((service, index) => (
              <ServiceCard 
                key={index} 
                service={service} 
                index={index} 
                isVisible={visibleItems.includes(index)}
              />
            ))}
          </div>
        </div>

        {/* Business Services */}
        <div className="mb-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <Briefcase className="w-7 h-7 text-purple-400" />
            </div>
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white">Pentru Companii</h3>
              <p className="text-slate-400 text-base">Soluții corporative profesionale</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {businessServices.map((service, index) => (
              <ServiceCard 
                key={index} 
                service={service} 
                index={index + 4} 
                isVisible={visibleItems.includes(index + 4)}
              />
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-slate-800/50 to-slate-800/50 border border-slate-700/50 rounded-2xl p-8 max-w-3xl mx-auto">
            <h4 className="text-2xl font-bold text-white mb-4">
              Cauți un Detectiv Particular în Bacău?
            </h4>
            <p className="text-slate-300 text-lg mb-6">
              Contactează-ne pentru o evaluare gratuită și confidențială. Detectivii Bacau sunt aici să te ajute!
            </p>
            <button
              onClick={scrollToContact}
              className="inline-flex items-center gap-3 bg-amber-500 hover:bg-amber-600 text-slate-900 
                font-bold text-lg px-8 py-4 rounded-xl transition-colors"
            >
              Solicită Consultație Gratuită
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
